import { createContext, useState, useEffect } from 'react';
import { enqueueSnackbar } from 'notistack';

import { getCartFromStorage, saveCartToStorage } from '../utils/cartStorage';

export const CartContext = createContext();

export const CartProvider = ({ children }) => {
    const [cart, setCart] = useState([]);

    // Initialize cart from localStorage
    useEffect(() => {
        const initializeCart = () => {
            const savedCart = getCartFromStorage();
            setCart(savedCart || []); // Ensure we always have an array
        };
        initializeCart();
    }, []);

    // Save to localStorage when cart changes
    useEffect(() => {
        saveCartToStorage(cart);
    }, [cart]);

    const addToCart = (product, quantity) => {
        setCart(prevCart => {
            const existingItem = prevCart.find(item => item.id_producto === product.id_producto);

            let newCart;
            
            if (existingItem) {
                newCart = prevCart.map(item =>
                    item.id_producto === product.id_producto
                        ? { ...item, quantity: item.quantity + quantity }
                        : item
                );
            } else {
                newCart = [...prevCart, { ...product, quantity }];
            }

            // Aplicar promoción si está activa
            if (product.promocion_activa) {
                // Calcular cuántos productos extra corresponden
                const totalQuantity = existingItem ? existingItem.quantity + quantity : quantity;
                const extraUnits = Math.floor(totalQuantity / product.productos_compra) * product.numero_productos_extra;

                // Buscar si el producto extra ya está en el carrito
                const existingExtra = newCart.find(item => item.id_producto === `${product.id_producto}-extra`);

                if (existingExtra) {
                    // Actualizar la cantidad del producto extra
                    newCart = newCart.map(item =>
                        item.id_producto === `${product.id_producto}-extra`
                            ? { ...item, quantity: extraUnits }
                            : item
                    );
                } else if (extraUnits > 0) {
                    // Agregar el producto extra si aún no existe
                    const extraProduct = {
                        ...product,
                        id_producto: `${product.id_producto}-extra`,
                        sku: product.sku_producto_extra,
                        descripcion: `${product.nombre_producto_extra} (Promo Extra)`,
                        quantity: extraUnits,
                        precio_1: "0.00", 
                        precio_descuento: "0.00",
                        isPromo: true 
                    };

                    newCart = [...newCart, extraProduct];
                }

                // Mostrar un mensaje si se aplicó la promoción
                if (extraUnits > 0) {
                    enqueueSnackbar(`¡Promoción activada! Has recibido ${extraUnits} extra(s) de ${product.nombre_producto_extra}`, {
                        variant: 'info',
                        autoHideDuration: 3000,
                        anchorOrigin: { vertical: 'bottom', horizontal: 'right' }
                    });
                }
            }

            return newCart;
        });

        enqueueSnackbar('Producto agregado al carrito', {
            variant: 'success',
            autoHideDuration: 2000,
            anchorOrigin: { vertical: 'bottom', horizontal: 'right' }
        });
    };


    // Add other cart operations (remove, update quantity, clear, etc.)
    const removeFromCart = (productId) => {
        setCart(prevCart => 
            prevCart.filter(item => item.id_producto !== productId && item.id_producto !== `${productId}-extra`)
        );

        enqueueSnackbar('Producto eliminado del carrito', {
            variant: 'success',
            autoHideDuration: 2000,
            anchorOrigin: { vertical: 'bottom', horizontal: 'right' }
        });
    };


    const updateQuantity = (productId, newQuantity) => {
        if (newQuantity <= 0) {
            removeFromCart(productId);
            return;
        }

        setCart(prevCart => {
            let updatedCart = prevCart.map(item =>
                item.id_producto === productId
                    ? { ...item, quantity: newQuantity }
                    : item
            );

            // Verificar si el producto tiene promoción activa
            const updatedItem = updatedCart.find(item => item.id_producto === productId);

            if (updatedItem && updatedItem.promocion_activa) {
                // Eliminar cualquier producto extra previamente agregado
                updatedCart = updatedCart.filter(item => item.id_producto !== `${productId}-extra`);

                // Calcular cuántos productos extra corresponden
                const extraUnits = Math.floor(newQuantity / updatedItem.productos_compra) * updatedItem.numero_productos_extra;

                if (extraUnits > 0) {
                    // Buscar si el producto extra ya está en el carrito
                    const existingExtra = updatedCart.find(item => item.id_producto === `${productId}-extra`);

                    if (existingExtra) {
                        // Actualizar cantidad del producto extra
                        updatedCart = updatedCart.map(item =>
                            item.id_producto === `${productId}-extra`
                                ? { ...item, quantity: extraUnits }
                                : item
                        );
                    } else {
                        // Agregar producto extra si aún no existe
                        const extraProduct = {
                            ...updatedItem,
                            id_producto: `${productId}-extra`,
                            sku: updatedItem.sku_producto_extra,
                            descripcion: `${updatedItem.nombre_producto_extra} (Promo Extra)`,
                            quantity: extraUnits,
                            precio_1: "0.00",
                            precio_descuento: "0.00",
                            isPromo: true
                        };

                        updatedCart.push(extraProduct);
                    }

                    enqueueSnackbar(`¡Promoción aplicada! Has recibido ${extraUnits} extra(s) de ${updatedItem.nombre_producto_extra}`, {
                        variant: 'info',
                        autoHideDuration: 3000,
                        anchorOrigin: { vertical: 'bottom', horizontal: 'right' }
                    });
                }
            }

            return updatedCart;
        });

        enqueueSnackbar('Cantidad actualizada', {
            variant: 'success',
            autoHideDuration: 2000,
            anchorOrigin: { vertical: 'bottom', horizontal: 'right' }
        });
    };

    const clearCart = () => {
        setCart([]);
    };

    return (
        <CartContext.Provider
            value={{
                cart,
                setCart,
                addToCart,
                removeFromCart,
                updateQuantity,
                clearCart
            }}
        >
            {children}
        </CartContext.Provider>
    );
};